import React, { useState } from 'react';
import classNames from 'classnames';

import { parseSelectFilterOptions } from '../../../util/search';
import { SCHEMA_TYPE_MULTI_ENUM } from '../../../util/types';

import { FieldCheckbox } from '../../../components';
import FieldRadioButton from '../../../components/FieldRadioButton/FieldRadioButton'; // ✅ ADD

import FilterListItem from '../FilterListItem/FilterListItem';
import FilterDetailModal from '../FilterDetailModal/FilterDetailModal';
import SearchableFilterOptions from '../SearchableFilterOptions/SearchableFilterOptions';
import FilterForm from '../FilterForm/FilterForm';

import css from './SelectMultipleFilterMobile.module.css';

// Filters like "league" or "club" can have hundreds/thousands of options.
// Rendering every one of them as a checkbox the instant the modal opens is
// what made opening/closing these filters freeze the page - so cap what
// renders up front and let the search box (which still searches the full
// option list) narrow it down instead.
const MAX_VISIBLE_OPTIONS_WITHOUT_SEARCH = 100;

// ✅ UPDATED GROUP COMPONENT
const GroupOfFieldCheckboxes = props => {
  const { id, className, name, options, searchTerm = '', isSingleSelect } = props;

  const filteredOptions = options.filter(
    optionConfig => optionConfig.option !== 'for_sale' && optionConfig.option !== 'not_for_sale'
  );

  const searchFilteredOptions = searchTerm
    ? filteredOptions.filter(optionConfig =>
        optionConfig.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : filteredOptions;

  const visibleOptions =
    !searchTerm && searchFilteredOptions.length > MAX_VISIBLE_OPTIONS_WITHOUT_SEARCH
      ? searchFilteredOptions.slice(0, MAX_VISIBLE_OPTIONS_WITHOUT_SEARCH)
      : searchFilteredOptions;
  const hiddenOptionsCount = searchFilteredOptions.length - visibleOptions.length;

  return (
    <fieldset className={className}>
      <ul className={css.list}>
        {visibleOptions.length > 0 ? (
          visibleOptions.map(optionConfig => {
            const { option, label, colorHex } = optionConfig;
            const fieldId = `${id}.${option}`;

            const labelContent = colorHex ? (
              <span className={css.labelWithColor}>
                <span className={css.colorCircle} style={{ backgroundColor: colorHex }} />
                {label}
              </span>
            ) : (
              label
            );

            return (
              <li key={fieldId} className={css.item}>
                {isSingleSelect ? (
                  <FieldRadioButton id={fieldId} name={name} label={labelContent} value={option} />
                ) : (
                  <FieldCheckbox id={fieldId} name={name} label={labelContent} value={option} />
                )}
              </li>
            );
          })
        ) : (
          <li className={css.noResults}>No options found</li>
        )}
        {hiddenOptionsCount > 0 ? (
          <li className={css.noResults}>+{hiddenOptionsCount} more - type to search</li>
        ) : null}
      </ul>
    </fieldset>
  );
};

const getQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;
};

const format = (selectedOptions, queryParamName, schemaType, searchMode) => {
  const hasOptionsSelected = selectedOptions && selectedOptions.length > 0;
  const mode = schemaType === SCHEMA_TYPE_MULTI_ENUM && searchMode ? `${searchMode}:` : '';
  const value = hasOptionsSelected ? `${mode}${selectedOptions.join(',')}` : null;
  return { [queryParamName]: value };
};

const SelectMultipleFilterMobile = props => {
  const {
    rootClassName,
    className,
    id,
    name,
    label,
    options,
    initialValues,
    onSubmit,
    queryParamNames,
    schemaType,
    searchMode,
    showAsModalMaxWidth,
    onManageDisableScrolling,
    isSingleSelect, // ✅ ADD
    resultsCount,
    ...rest
  } = props;

  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const classes = classNames(rootClassName || css.root, className);

  const queryParamName = getQueryParamName(queryParamNames);
  const hasInitialValues = !!initialValues && !!initialValues[queryParamName];

  const parsedOptions = hasInitialValues
    ? parseSelectFilterOptions(initialValues[queryParamName])
    : [];

  // Id-only match - see SelectMultipleFilter.js for why label matching was
  // removed (it made a typed search word that happens to equal an option's
  // label show up as a pre-checked filter it never selected).
  const optionValuesSet = new Set((options || []).map(o => `${o.option}`));

  const selectedOptions = parsedOptions.filter(p => optionValuesSet.has(`${p}`));

  const selectedCount = selectedOptions.length;

  const namedInitialValues = {
    [name]: isSingleSelect ? selectedOptions[0] || null : selectedOptions,
  };

  // ✅ UPDATED SUBMIT HANDLER
  const handleSubmit = values => {
    const usedValue = values ? values[name] : values;

    const finalValue = isSingleSelect ? (usedValue ? [usedValue] : []) : usedValue;

    onSubmit(format(finalValue, queryParamName, schemaType, searchMode));

    // ✅ OPTIONAL UX: auto close modal
    if (isSingleSelect) {
      handleCloseDetail();
    }
  };

  const handleOpenDetail = () => {
    setIsDetailModalOpen(true);
  };

  const handleCloseDetail = () => {
    setIsDetailModalOpen(false);
    setSearchTerm('');
  };

  const searchableFilters = ['country', 'league', 'club', 'player', 'season', 'brand'];
  const shouldShowSearch = searchableFilters.includes(name);

  const filterContent = shouldShowSearch ? (
    <SearchableFilterOptions
      onSearch={setSearchTerm}
      placeholder="Search options..."
      searchTerm={searchTerm}
    >
      <GroupOfFieldCheckboxes
        className={css.fieldGroup}
        name={name}
        id={`${id}-checkbox-group`}
        options={options}
        searchTerm={searchTerm}
        isSingleSelect={isSingleSelect} // ✅ PASS
      />
    </SearchableFilterOptions>
  ) : (
    <GroupOfFieldCheckboxes
      className={css.fieldGroup}
      name={name}
      id={`${id}-checkbox-group`}
      options={options}
      isSingleSelect={isSingleSelect} // ✅ PASS
    />
  );

  return (
    <>
      <FilterListItem
        label={label}
        selectedCount={selectedCount}
        onClick={handleOpenDetail}
        className={classes}
      />

      <FilterDetailModal
        id={id}
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetail}
        title={label}
        showAsModalMaxWidth={showAsModalMaxWidth}
        onManageDisableScrolling={onManageDisableScrolling}
        resultsCount={resultsCount}
      >
        <FilterForm
          id={`${id}.form`}
          liveEdit
          onChange={handleSubmit}
          onSubmit={handleSubmit}
          initialValues={namedInitialValues}
          keepDirtyOnReinitialize
          {...rest}
        >
          {filterContent}
        </FilterForm>
      </FilterDetailModal>
    </>
  );
};

export default SelectMultipleFilterMobile;
